import { useState, useMemo } from 'react';
import { useTransportJobs } from '@/hooks/useTransportJobs';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { EmptyState } from '@/components/dashboard/EmptyState';
import { MissionCard } from '@/components/transporter/MissionCard';
import { AssignMissionDialog } from '@/components/transporter/AssignMissionDialog';
import { MissionDetailsDialog } from '@/components/transporter/MissionDetailsDialog';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, X, List, LayoutGrid, MapPin, Calendar, Filter } from 'lucide-react';
import { MissionCardSkeleton } from '@/components/LoadingSkeletons';
import { useTranslation } from 'react-i18next';
import { TransportJob } from '@/hooks/useTransportJobs';

const Missions = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { jobs, isLoading } = useTransportJobs();
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string>('');
  const [selectedPickupDate, setSelectedPickupDate] = useState<string>('');
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<string>('');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<TransportJob | null>(null);
  const [trailerTypeFilter, setTrailerTypeFilter] = useState<string>('all');
  const [distanceFilter, setDistanceFilter] = useState<string>('all');

  const handleOpenAssignDialog = (
    jobId: string, 
    orderNumber: string, 
    pickupDate: string, 
    deliveryDate: string,
    vehicleId?: string,
    driverId?: string
  ) => {
    if (!jobId || !orderNumber) {
      console.error('Invalid jobId or orderNumber:', { jobId, orderNumber });
      return;
    }
    setSelectedJobId(jobId);
    setSelectedOrderNumber(orderNumber);
    setSelectedPickupDate(pickupDate);
    setSelectedDeliveryDate(deliveryDate);
    setSelectedVehicleId(vehicleId || '');
    setSelectedDriverId(driverId || '');
    setAssignDialogOpen(true);
  };

  // Calculate distance between two coordinates using Haversine formula
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Radius of the Earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  };

  // Get unique values for filters
  const uniqueTrailerTypes = useMemo(() => {
    const types = new Set<string>();
    jobs.forEach(job => {
      if (job.orders?.trailer_type) {
        types.add(job.orders.trailer_type);
      }
    });
    return Array.from(types).sort();
  }, [jobs]);

  // Filtrer les missions par terme de recherche et filtres
  const filteredJobs = jobs.filter((job) => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        job.orders?.order_number?.toLowerCase().includes(searchLower) ||
        job.orders?.cargo_title?.toLowerCase().includes(searchLower) ||
        job.orders?.cargo_description?.toLowerCase().includes(searchLower) ||
        job.orders?.pickup_city?.toLowerCase().includes(searchLower) ||
        job.orders?.delivery_city?.toLowerCase().includes(searchLower) ||
        job.orders?.pickup_address?.toLowerCase().includes(searchLower) ||
        job.orders?.delivery_address?.toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }

    // Trailer type filter
    if (trailerTypeFilter !== 'all' && job.orders?.trailer_type !== trailerTypeFilter) {
      return false;
    }

    // Distance filter
    if (distanceFilter !== 'all' && job.orders?.pickup_lat && job.orders?.pickup_lng && job.orders?.delivery_lat && job.orders?.delivery_lng) {
      const distance = calculateDistance(
        Number(job.orders.pickup_lat),
        Number(job.orders.pickup_lng),
        Number(job.orders.delivery_lat),
        Number(job.orders.delivery_lng)
      );

      switch (distanceFilter) {
        case '0-100':
          if (distance >= 100) return false;
          break;
        case '100-500':
          if (distance < 100 || distance >= 500) return false;
          break;
        case '500-1000':
          if (distance < 500 || distance >= 1000) return false;
          break;
        case '1000+':
          if (distance < 1000) return false;
          break;
      }
    }

    return true;
  });

  const hasActiveFilters = trailerTypeFilter !== 'all' || distanceFilter !== 'all';

  const clearFilters = () => {
    setTrailerTypeFilter('all');
    setDistanceFilter('all');
  };

  // Filtrer les missions par statut
  const pendingJobs = filteredJobs.filter(
    (job) => !job.driver_id && !job.vehicle_id && !job.started_at && !job.completed_at
  );
  const activeJobs = filteredJobs.filter(
    (job) => (job.driver_id || job.vehicle_id) && !job.completed_at
  );
  const completedJobs = filteredJobs.filter((job) => job.completed_at);

  const handleOpenDetailsDialog = (job: TransportJob) => {
    setSelectedJob(job);
    setDetailsDialogOpen(true);
  };

  const getStatusBadge = (job: TransportJob) => {
    if (job.completed_at) {
      return <Badge className="bg-[#14A38B] text-white">{t('missions.card.status.completed')}</Badge>;
    }
    if (job.started_at) {
      return <Badge className="bg-[#0880AE] text-white">{t('missions.card.status.inProgress')}</Badge>;
    }
    if (job.assigned_at || (job.driver_id && job.vehicle_id)) {
      return <Badge className="bg-[#37699E] text-white">{t('missions.card.status.assigned')}</Badge>;
    }
    return <Badge className="bg-[#F2AC57] text-white">{t('missions.card.status.pending')}</Badge>;
  };

  const renderGridView = (jobsList: TransportJob[]) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {jobsList.map((job) => (
        <Card
          key={job.id}
          className="rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-[#DBE2EA] cursor-pointer"
          onClick={() => handleOpenDetailsDialog(job)}
        >
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-medium text-[#37699E] font-heading line-clamp-2">
                {job.orders?.cargo_title || job.orders?.cargo_description}
              </h3>
              {getStatusBadge(job)}
            </div>
            
            <p className="text-sm text-[#5A6B7A]">{t('missions.card.order')} {job.orders?.order_number}</p>

            <div className="space-y-2 pt-2 border-t border-[#DBE2EA]">
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-[#0880AE] flex-shrink-0" />
                <p className="text-sm text-[#2D3748] truncate">{job.orders?.pickup_city}</p>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-[#14A38B] flex-shrink-0" />
                <p className="text-sm text-[#2D3748] truncate">{job.orders?.delivery_city}</p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-[#DBE2EA]">
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-[#5A6B7A]" />
                <p className="text-xs text-[#5A6B7A]">
                  {job.orders?.pickup_date && new Date(job.orders.pickup_date).toLocaleDateString()}
                </p>
              </div>
              <p className="text-lg font-semibold text-[#37699E]">
                {job.orders?.final_price || job.orders?.proposed_price} €
              </p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderListView = (jobsList: TransportJob[]) => (
    <div className="grid grid-cols-1 gap-6">
      {jobsList.map((job) => (
        <MissionCard
          key={job.id}
          job={job}
          onAssign={() =>
            handleOpenAssignDialog(
              job.id, 
              job.orders?.order_number || '', 
              job.orders?.pickup_date || '',
              job.orders?.delivery_date || '',
              job.vehicle_id || undefined,
              job.driver_id || undefined
            )
          }
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <DashboardHeader title={t('missions.title')} userName={user?.name || user?.email} />
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' 
                ? 'bg-[#37699E] text-white hover:bg-[#2E5782]' 
                : 'border-[#DBE2EA] text-[#5A6B7A] hover:bg-[#EBF4F8]'
              }
            >
              <List size={18} />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' 
                ? 'bg-[#37699E] text-white hover:bg-[#2E5782]' 
                : 'border-[#DBE2EA] text-[#5A6B7A] hover:bg-[#EBF4F8]'
              }
            >
              <LayoutGrid size={18} />
            </Button>
          </div>
        </div>

        <Card className="rounded-xl shadow-md">
          <CardContent className="p-6">
            {/* Search bar */}
            <div className="space-y-4 mb-6">
              <div className="relative">
                <SearchInput
                  placeholder={t('missions.search') || 'Rechercher par numéro, ville ou adresse...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pr-12 w-full rounded-lg border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#87ADD4] hover:text-[#0880AE] transition-colors"
                    aria-label="Effacer la recherche"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-[#5A6B7A]" />
                  <span className="text-sm font-medium text-[#5A6B7A]">{t('missions.filters.title')}</span>
                </div>

                <Select value={trailerTypeFilter} onValueChange={setTrailerTypeFilter}>
                  <SelectTrigger className="w-[200px] rounded-lg border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20">
                    <SelectValue placeholder={t('missions.filters.trailerType')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="all">{t('missions.filters.allTrailers')}</SelectItem>
                    {uniqueTrailerTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={distanceFilter} onValueChange={setDistanceFilter}>
                  <SelectTrigger className="w-[200px] rounded-lg border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20">
                    <SelectValue placeholder={t('missions.filters.distance')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="all">{t('missions.filters.allDistances')}</SelectItem>
                    <SelectItem value="0-100">{t('missions.filters.distance0to100')}</SelectItem>
                    <SelectItem value="100-500">{t('missions.filters.distance100to500')}</SelectItem>
                    <SelectItem value="500-1000">{t('missions.filters.distance500to1000')}</SelectItem>
                    <SelectItem value="1000+">{t('missions.filters.distance1000plus')}</SelectItem>
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-[#5A6B7A] hover:text-[#0880AE] hover:bg-[#EBF4F8]"
                  >
                    <X size={16} className="mr-1" />
                    {t('missions.filters.clearFilters')}
                  </Button>
                )}
              </div>

              {(searchTerm || hasActiveFilters) && (
                <p className="text-sm text-[#5A6B7A]">
                  {filteredJobs.length} {filteredJobs.length === 1 ? t('missions.resultFound') || 'résultat trouvé' : t('missions.resultsFound') || 'résultats trouvés'}
                </p>
              )}
            </div>

            <Tabs defaultValue="all" className="space-y-6">
              <TabsList className="grid w-full grid-cols-4 bg-[#EBF4F8] rounded-lg p-1">
                <TabsTrigger
                  value="all"
                  className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#37699E]"
                >
                  {t('missions.all')} ({filteredJobs.length})
                </TabsTrigger>
                <TabsTrigger
                  value="pending"
                  className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#37699E]"
                >
                  {t('missions.pending')} ({pendingJobs.length})
                </TabsTrigger>
                <TabsTrigger
                  value="active"
                  className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#37699E]"
                >
                  {t('missions.active')} ({activeJobs.length})
                </TabsTrigger>
                <TabsTrigger
                  value="completed"
                  className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#37699E]"
                >
                  {t('missions.completed')} ({completedJobs.length})
                </TabsTrigger>
              </TabsList>

              {isLoading ? (
                <div className="grid grid-cols-1 gap-6">
                  {[...Array(3)].map((_, i) => (
                    <MissionCardSkeleton key={i} />
                  ))}
                </div>
              ) : (
                <>
                <TabsContent value="all" className="space-y-6">
                    {filteredJobs.length === 0 ? (
                      <EmptyState message={searchTerm ? (t('missions.noResults') || 'Aucune mission ne correspond à votre recherche') : t('missions.noMissions')} icon={Package} />
                    ) : (
                      viewMode === 'list' ? renderListView(filteredJobs) : renderGridView(filteredJobs)
                    )}
                  </TabsContent>

                  <TabsContent value="pending" className="space-y-6">
                    {pendingJobs.length === 0 ? (
                      <EmptyState message={t('missions.noMissionPending')} icon={Package} />
                    ) : (
                      viewMode === 'list' ? renderListView(pendingJobs) : renderGridView(pendingJobs)
                    )}
                  </TabsContent>

                  <TabsContent value="active" className="space-y-6">
                    {activeJobs.length === 0 ? (
                      <EmptyState message={t('missions.noMissionActive')} icon={Package} />
                    ) : (
                      viewMode === 'list' ? renderListView(activeJobs) : renderGridView(activeJobs)
                    )}
                  </TabsContent>

                  <TabsContent value="completed" className="space-y-6">
                    {completedJobs.length === 0 ? (
                      <EmptyState message={t('missions.noMissionCompleted')} icon={Package} />
                    ) : (
                      viewMode === 'list' ? renderListView(completedJobs) : renderGridView(completedJobs)
                    )}
                  </TabsContent>
                </>
              )}
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {selectedJobId && (
        <AssignMissionDialog
          open={assignDialogOpen}
          onOpenChange={setAssignDialogOpen}
          transportJobId={selectedJobId}
          orderNumber={selectedOrderNumber}
          pickupDate={selectedPickupDate}
          deliveryDate={selectedDeliveryDate}
          currentVehicleId={selectedVehicleId}
          currentDriverId={selectedDriverId}
        />
      )}

      <MissionDetailsDialog
        job={selectedJob}
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
        onAssign={() => {
          if (selectedJob) {
            handleOpenAssignDialog(
              selectedJob.id,
              selectedJob.orders?.order_number || '',
              selectedJob.orders?.pickup_date || '',
              selectedJob.orders?.delivery_date || '',
              selectedJob.vehicle_id || undefined,
              selectedJob.driver_id || undefined
            );
          }
        }}
      />
    </div>
  );
};

export default Missions;
